import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus, X } from 'lucide-react';
import { formatMoney, formatQty, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { ProductDto } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { ProductPicker, QtyInput, Tabs } from '../../ui/inputs';
import { PeriodPicker, ProductQtyLines, usePeriod, type QtyLine } from '../../ui/period';

interface BreakdownRow {
  id: string;
  number: string;
  createdAt: string;
  sourceName: string;
  branchName: string;
  sourceQtyMilli: number;
  outputQtyMilli: number;
  yieldBp: number;
}
interface BreakdownDetail extends BreakdownRow {
  sourceCostCents: number | null;
  userName: string | null;
  note: string | null;
  lossMilli: number;
  outputs: { productId: string; name: string; qtyMilli: number; yieldBp: number; allocatedCostCents: number | null; costPerKgCents: number | null; saleValueCents: number }[];
}
interface YieldRow {
  sourceId: string;
  source: string;
  breakdowns: number;
  inputMilli: number;
  outputMilli: number;
  costCents: number | null;
  yieldBp: number;
  lossBp: number;
  cuts: { name: string; qtyMilli: number; yieldBp: number; costPerKgCents: number | null }[];
}

const pct = (bp: number) => `${(bp / 100).toFixed(1)}%`;

export function BreakdownsPage() {
  const { can } = useSession();
  const [tab, setTab] = useState<'list' | 'yields'>('list');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  return (
    <>
      <PageHeader
        title="Carcass breakdown"
        description="Cut a carcass, side or quarter into retail cuts. Its cost is shared between the cuts by their selling value, so the profit on each cut is real."
        actions={can('yields.manage') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>Record a breakdown</Button>}
      />
      <Tabs value={tab} onChange={setTab} tabs={[{ value: 'list', label: 'Breakdowns' }, { value: 'yields', label: 'Yields' }]} />
      {tab === 'list' ? <BreakdownList onOpen={setViewing} /> : <YieldReport />}
      {creating && <BreakdownDialog onClose={() => setCreating(false)} onDone={(id) => { setCreating(false); setViewing(id); }} />}
      {viewing && <BreakdownDetailDialog id={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function BreakdownList({ onOpen }: { onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['breakdowns'], queryFn: () => api.get<BreakdownRow[]>('/api/breakdowns') });
  return (
    <Panel>
      {q.isLoading && <Spinner />}
      {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
      {q.data?.length === 0 && <EmptyState title="No breakdowns yet">Record one each time a carcass is cut up.</EmptyState>}
      {!!q.data?.length && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-line text-sm text-muted">
                <th className="px-5 py-2.5 font-medium">Breakdown</th>
                <th className="px-3 py-2.5 font-medium">Cut up</th>
                <th className="px-3 py-2.5 text-right font-medium">Weight in</th>
                <th className="px-3 py-2.5 text-right font-medium">Cuts out</th>
                <th className="px-5 py-2.5 text-right font-medium">Yield</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {q.data.map((b) => (
                <tr key={b.id} className="hover:bg-ledger-50/60">
                  <td className="px-5 py-2.5">
                    <button className="font-medium hover:underline" onClick={() => onOpen(b.id)}>{b.number}</button>
                    <span className="block text-xs text-muted">{formatDateTime(b.createdAt)} — {b.branchName}</span>
                  </td>
                  <td className="px-3 py-2.5">{b.sourceName}</td>
                  <td className="px-3 py-2.5 text-right num">{formatQty(b.sourceQtyMilli)} kg</td>
                  <td className="px-3 py-2.5 text-right num">{formatQty(b.outputQtyMilli)} kg</td>
                  <td className="px-5 py-2.5 text-right num font-medium">{pct(b.yieldBp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function YieldReport() {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const period = usePeriod('30d');
  const q = useQuery({ queryKey: ['yields', period.from, period.to], queryFn: () => api.get<YieldRow[]>(`/api/reports/yields?from=${period.from}&to=${period.to}`) });
  return (
    <>
      <div className="mb-4"><PeriodPicker period={period} /></div>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} />}
      {q.data?.length === 0 && <Panel><EmptyState title="No breakdowns in this period" /></Panel>}
      <div className="space-y-6">
        {q.data?.map((r) => (
          <Panel key={r.sourceId} title={r.source}>
            <div className="grid gap-4 border-b border-line px-5 py-4 sm:grid-cols-4">
              <Figure label="Breakdowns" value={String(r.breakdowns)} />
              <Figure label="Weight cut up" value={`${formatQty(r.inputMilli)} kg`} />
              <Figure label="Saleable yield" value={pct(r.yieldBp)} />
              <Figure label="Cutting loss" value={pct(r.lossBp)} tone={r.lossBp > 3000 ? 'warn' : undefined} />
            </div>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Cut</th>
                  <th className="px-3 py-2.5 text-right font-medium">Weight</th>
                  <th className="px-3 py-2.5 text-right font-medium">Share of carcass</th>
                  {r.cuts[0]?.costPerKgCents != null && <th className="px-5 py-2.5 text-right font-medium">Cost per kg</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {r.cuts.map((c) => (
                  <tr key={c.name}>
                    <td className="px-5 py-2.5">{c.name}</td>
                    <td className="px-3 py-2.5 text-right num">{formatQty(c.qtyMilli)} kg</td>
                    <td className="px-3 py-2.5 text-right num">{pct(c.yieldBp)}</td>
                    {c.costPerKgCents != null && <td className="px-5 py-2.5 text-right num">{formatMoney(c.costPerKgCents, cur)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        ))}
      </div>
    </>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div>
      <p className="text-sm text-muted">{label}</p>
      <p className={clsx('text-xl font-semibold num', tone === 'warn' && 'text-warn')}>{value}</p>
    </div>
  );
}

function BreakdownDialog({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const qc = useQueryClient();
  const toast = useToast();
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [source, setSource] = useState<ProductDto | null>(null);
  const [sourceQty, setSourceQty] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [cuts, setCuts] = useState<QtyLine[]>([]);
  const out = cuts.reduce((a, c) => a + (c.qtyMilli ?? 0), 0);
  const over = !!sourceQty && out > sourceQty;
  const m = useMutation({
    mutationFn: () =>
      api.post<BreakdownDetail>('/api/breakdowns', {
        branchId,
        sourceProductId: source!.id,
        sourceQtyMilli: sourceQty,
        note,
        outputs: cuts.map((c) => ({ productId: c.product.id, qtyMilli: c.qtyMilli })),
      }),
    onSuccess: (r) => {
      toast(`${r.number} recorded: ${pct(r.yieldBp)} yield`);
      for (const k of ['breakdowns', 'yields', 'products', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onDone(r.id);
    },
  });
  const f = fieldErrors(m.error);
  const ready = branchId && source && sourceQty && cuts.length > 0 && cuts.every((c) => c.qtyMilli) && !over;
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Record a breakdown"
      description="Weigh what goes onto the block, then weigh each cut that comes off it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready} busy={m.isPending} onClick={() => m.mutate()}>Record breakdown</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
        <Field label="Branch" htmlFor="bd-branch">
          <Select id="bd-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Note (optional)" htmlFor="bd-note">
          <TextInput id="bd-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Grade A side from Koala Park" />
        </Field>
        <div className="sm:col-span-2 grid gap-4 sm:grid-cols-[1fr_180px]">
          {source ? (
            <div>
              <p className="mb-1.5 text-sm font-medium">What was cut up</p>
              <div className="flex h-10 items-center justify-between rounded-[var(--radius-control)] border border-line-strong bg-paper px-3">
                <span className="truncate">
                  {source.name}
                  {source.stock && <span className="text-sm text-muted"> — {formatQty(source.stock.qtyMilli)} kg in stock</span>}
                </span>
                <button className="rounded p-1 text-muted hover:text-ink" aria-label="Change" onClick={() => setSource(null)}><X className="size-4" /></button>
              </div>
            </div>
          ) : (
            <ProductPicker label="What was cut up" placeholder="Carcass, side or quarter" branchId={branchId} sellable={false} stockedOnly onPick={setSource} />
          )}
          <Field label="Weight in" htmlFor="bd-qty" error={f.sourceQtyMilli}>
            <QtyInput id="bd-qty" unit="kg" value={sourceQty} onChange={setSourceQty} />
          </Field>
        </div>
      </div>
      <div className="mt-5">
        <ProductQtyLines lines={cuts} onChange={setCuts} branchId={branchId} pickerLabel="Cuts that came off" emptyText="Add each cut and its weight: steaks, mince, stewing beef, bones." />
      </div>
      {(f.outputs || over) && <p className="mt-2 text-sm text-danger">{over ? 'The cuts weigh more than what was cut up.' : f.outputs}</p>}
      {sourceQty && out > 0 && !over && (
        <p className="mt-3 rounded-[8px] bg-paper px-4 py-3 text-sm">
          Yield <span className="font-semibold num">{pct(Math.round((out * 10_000) / sourceQty))}</span> — cutting loss{' '}
          <span className="num">{formatQty(sourceQty - out)} kg</span>
        </p>
      )}
    </Dialog>
  );
}

function BreakdownDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const q = useQuery({ queryKey: ['breakdown', id], queryFn: () => api.get<BreakdownDetail>(`/api/breakdowns/${id}`) });
  const d = q.data;
  return (
    <Dialog open wide onClose={onClose} title={d ? `${d.number}: ${d.sourceName}` : 'Breakdown'} description={d ? `${formatDateTime(d.createdAt)} at ${d.branchName}, by ${d.userName ?? '—'}${d.note ? `. ${d.note}` : ''}` : undefined}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} />}
      {d && (
        <>
          <div className="mb-5 grid gap-4 sm:grid-cols-4">
            <Figure label="Weight in" value={`${formatQty(d.sourceQtyMilli)} kg`} />
            <Figure label="Cuts out" value={`${formatQty(d.outputQtyMilli)} kg`} />
            <Figure label="Yield" value={pct(d.yieldBp)} />
            {d.sourceCostCents != null ? <Figure label="Carcass cost" value={formatMoney(d.sourceCostCents, cur)} /> : <Figure label="Loss" value={`${formatQty(d.lossMilli)} kg`} />}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="py-2 pr-3 font-medium">Cut</th>
                  <th className="px-3 py-2 text-right font-medium">Weight</th>
                  <th className="px-3 py-2 text-right font-medium">Yield</th>
                  <th className="px-3 py-2 text-right font-medium">Selling value</th>
                  {d.sourceCostCents != null && <th className="px-3 py-2 text-right font-medium">Cost share</th>}
                  {d.sourceCostCents != null && <th className="py-2 pl-3 text-right font-medium">Cost per kg</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.outputs.map((o) => (
                  <tr key={o.productId}>
                    <td className="py-2 pr-3">{o.name}</td>
                    <td className="px-3 py-2 text-right num">{formatQty(o.qtyMilli)} kg</td>
                    <td className="px-3 py-2 text-right num">{pct(o.yieldBp)}</td>
                    <td className="px-3 py-2 text-right num">{formatMoney(o.saleValueCents, cur)}</td>
                    {o.allocatedCostCents != null && <td className="px-3 py-2 text-right num">{formatMoney(o.allocatedCostCents, cur)}</td>}
                    {o.costPerKgCents != null && <td className="py-2 pl-3 text-right num">{formatMoney(o.costPerKgCents, cur)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.sourceCostCents != null && (
            <p className="mt-4 text-sm text-muted">
              The {formatMoney(d.sourceCostCents, cur)} carcass cost is shared by selling value: a cut worth twice as much carries twice the cost. The{' '}
              {formatQty(d.lossMilli)} kg cutting loss has no stock value; its cost sits in the cuts.
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
